import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Navbar } from '../components/Navbar'
import { AuthModal } from '../components/AuthModal'
import { propertiesAPI, offeringsAPI, subscriptionsAPI } from '../lib/api'
import { useAuth } from '../context/AuthContext'
import { useCurrency } from '../context/CurrencyContext'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog'
import { toast } from 'sonner'
import { 
  MapPin, TrendingUp, Clock, FileText, Building2, ArrowLeft,
  Wallet, Shield, AlertCircle, CheckCircle2, Loader2, ExternalLink, Copy
} from 'lucide-react'

const statusColors = {
  active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  tokenized: 'bg-blue-100 text-blue-700 border-blue-200',
  sold_out: 'bg-red-100 text-red-700 border-red-200',
  draft: 'bg-slate-100 text-slate-600 border-slate-200',
}

export function PropertyDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { isAuthenticated, isKYCApproved } = useAuth()
  const { formatPrice, currencyInfo, convert } = useCurrency()
  
  const [property, setProperty] = useState(null)
  const [offering, setOffering] = useState(null)
  const [loading, setLoading] = useState(true)
  const [authOpen, setAuthOpen] = useState(false)
  const [investOpen, setInvestOpen] = useState(false)
  const [investAmount, setInvestAmount] = useState('')
  const [investLoading, setInvestLoading] = useState(false)

  useEffect(() => {
    const loadData = async () => {
      try {
        const propRes = await propertiesAPI.get(id)
        setProperty(propRes.data)
        const offRes = await offeringsAPI.list('active')
        const propOffering = offRes.data.find(o => o.property_id === id)
        if (propOffering) setOffering(propOffering)
      } catch (error) {
        console.error('Failed to load property:', error)
        toast.error('Property not found')
        navigate('/marketplace')
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [id, navigate])

  const handleInvest = async () => {
    if (!isAuthenticated) { setAuthOpen(true); return }
    if (!isKYCApproved) { toast.error('Please complete KYC verification first'); navigate('/kyc'); return }
    setInvestOpen(true)
  }

  const submitInvestment = async () => {
    if (!offering) return
    const amount = parseFloat(investAmount)
    const minInvConverted = convert(property.min_investment)
    if (isNaN(amount) || amount < minInvConverted) {
      toast.error(`Minimum investment is ${formatPrice(property.min_investment)}`)
      return
    }
    setInvestLoading(true)
    try {
      const amountUSD = amount / (convert(1))
      const tokenCount = Math.floor(amountUSD / property.token_price)
      await subscriptionsAPI.create({ offering_id: offering.id, amount: amountUSD, token_count: tokenCount })
      toast.success('Investment submitted successfully!')
      setInvestOpen(false)
      setInvestAmount('')
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Investment failed')
    } finally {
      setInvestLoading(false)
    }
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
    toast.success('Copied to clipboard')
  }

  if (loading) {
    return (<div className="min-h-screen bg-[#F8FAFC]"><Navbar /><div className="flex items-center justify-center pt-32"><Loader2 className="w-8 h-8 animate-spin text-brand-primary" /></div></div>)
  }
  if (!property) return null

  const progress = property.total_tokens > 0 ? ((property.tokens_sold || 0) / property.total_tokens) * 100 : 0
  const heroImage = property.images?.[0] || 'https://images.unsplash.com/photo-1760611655987-d348d6d28174?w=1200&q=80'

  const investAmountNum = parseFloat(investAmount) || 0
  const investAmountUSD = investAmountNum / (convert(1))
  const tokensToReceive = Math.floor(investAmountUSD / property.token_price) || 0
  const expectedReturn = (investAmountUSD * property.expected_yield) / 100

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <main className="pt-20 pb-16">
        <div className="relative h-[450px] overflow-hidden">
          <img src={heroImage} alt={property.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-secondary via-brand-secondary/30 to-transparent" />
          <Button onClick={() => navigate('/marketplace')} variant="ghost"
            className="absolute top-24 left-4 sm:left-8 text-white hover:bg-white/20 rounded-none" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />{t('property.backToMarketplace')}
          </Button>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-32 relative">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-slate-100 p-8">
                <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                  <div>
                    <span className={`inline-block px-2 py-1 text-xs font-semibold uppercase tracking-widest border mb-4 ${statusColors[property.status]}`}>{property.status?.replace('_', ' ')}</span>
                    <h1 className="text-3xl font-heading text-brand-secondary" data-testid="property-name">{property.name}</h1>
                    <div className="flex items-center gap-2 mt-2 text-slate-500"><MapPin className="w-4 h-4" /><span>{property.address}, {property.city}, {property.country}</span></div>
                  </div>
                </div>
                <div className="w-16 h-px bg-brand-primary my-6" />
                <p className="text-slate-600 leading-relaxed">{property.description}</p>

                {property.contract_address && (
                  <div className="mt-6 p-4 bg-[#F8FAFC] border border-slate-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{t('property.tokenContract')}</p>
                        <code className="text-sm text-slate-600 font-mono">{property.contract_address.slice(0, 12)}...{property.contract_address.slice(-8)}</code>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" onClick={() => copyToClipboard(property.contract_address)} className="text-slate-500 hover:text-brand-secondary rounded-none"><Copy className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" className="text-slate-500 hover:text-brand-secondary rounded-none"><ExternalLink className="w-4 h-4" /></Button>
                      </div>
                    </div>
                    <p className="text-xs text-slate-400 mt-2">Symbol: <span className="text-brand-primary font-semibold">{property.token_symbol}</span> • ERC-3643 (Mock)</p>
                  </div>
                )}
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white border border-slate-100">
                <Tabs defaultValue="overview">
                  <TabsList className="w-full border-b border-slate-100 bg-transparent p-0 h-auto">
                    <TabsTrigger value="overview" className="flex-1 py-4 rounded-none border-b-2 border-transparent data-[state=active]:border-brand-primary data-[state=active]:bg-transparent text-slate-500 data-[state=active]:text-brand-secondary font-semibold">{t('property.overview')}</TabsTrigger>
                    <TabsTrigger value="financials" className="flex-1 py-4 rounded-none border-b-2 border-transparent data-[state=active]:border-brand-primary data-[state=active]:bg-transparent text-slate-500 data-[state=active]:text-brand-secondary font-semibold">{t('property.financials')}</TabsTrigger>
                    <TabsTrigger value="documents" className="flex-1 py-4 rounded-none border-b-2 border-transparent data-[state=active]:border-brand-primary data-[state=active]:bg-transparent text-slate-500 data-[state=active]:text-brand-secondary font-semibold">{t('property.documents')}</TabsTrigger>
                  </TabsList>
                  <TabsContent value="overview" className="p-6">
                    <div className="grid grid-cols-2 gap-6">
                      <div className="space-y-1"><p className="text-xs text-slate-500 uppercase tracking-wider">{t('property.propertyType')}</p><p className="text-brand-secondary capitalize font-semibold">{property.property_type?.replace('_', ' ')}</p></div>
                      <div className="space-y-1"><p className="text-xs text-slate-500 uppercase tracking-wider">{t('property.investmentTerm')}</p><p className="text-brand-secondary font-semibold">{property.investment_term} {t('property.months')}</p></div>
                      <div className="space-y-1"><p className="text-xs text-slate-500 uppercase tracking-wider">{t('property.totalTokens')}</p><p className="text-brand-secondary font-mono font-semibold">{property.total_tokens?.toLocaleString()}</p></div>
                      <div className="space-y-1"><p className="text-xs text-slate-500 uppercase tracking-wider">{t('property.tokensSold')}</p><p className="text-brand-secondary font-mono font-semibold">{property.tokens_sold?.toLocaleString()}</p></div>
                    </div>
                  </TabsContent>
                  <TabsContent value="financials" className="p-6">
                    {property.financials && Object.entries(property.financials).length > 0 ? (
                      Object.entries(property.financials).map(([key, value]) => (
                        <div key={key} className="flex justify-between py-3 border-b border-slate-100 last:border-0">
                          <span className="text-slate-500 capitalize">{key.replace(/_/g, ' ')}</span>
                          <span className="text-brand-secondary font-mono font-semibold">{typeof value === 'number' ? formatPrice(value) : value}</span>
                        </div>
                      ))
                    ) : (<p className="text-slate-400 text-center py-8">{t('property.financialsComingSoon')}</p>)}
                  </TabsContent>
                  <TabsContent value="documents" className="p-6">
                    {property.documents && property.documents.length > 0 ? (
                      property.documents.map((doc, index) => (
                        <div key={index} className="flex items-center justify-between p-4 bg-[#F8FAFC] border border-slate-100">
                          <div className="flex items-center gap-3"><FileText className="w-5 h-5 text-brand-primary" /><span className="text-brand-secondary font-semibold">{doc.name || `Document ${index + 1}`}</span></div>
                          <Button size="sm" variant="ghost" className="text-brand-primary hover:text-[#C5A028] rounded-none">View</Button>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8"><FileText className="w-12 h-12 mx-auto text-slate-300 mb-3" /><p className="text-slate-400">{t('property.documentsComingSoon')}</p></div>
                    )}
                  </TabsContent>
                </Tabs>
              </motion.div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="sticky top-24 bg-white border border-slate-100 p-6">
                <h3 className="text-lg font-heading text-brand-secondary mb-6">{t('property.investmentSummary')}</h3>
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between"><span className="text-slate-500">{t('property.totalValue')}</span><span className="text-brand-secondary font-mono font-semibold" data-testid="detail-total-value">{formatPrice(property.total_value)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">{t('property.tokenPrice')}</span><span className="text-brand-secondary font-mono font-semibold" data-testid="detail-token-price">{formatPrice(property.token_price, { decimals: 2 })}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">{t('property.minInvestment')}</span><span className="text-brand-primary font-mono font-semibold">{formatPrice(property.min_investment)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">{t('property.expectedYield')}</span><span className="text-emerald-600 font-mono font-semibold flex items-center gap-1"><TrendingUp className="w-4 h-4" />{property.expected_yield}% {t('common.apy')}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">{t('property.term')}</span><span className="text-brand-secondary font-semibold flex items-center gap-1"><Clock className="w-4 h-4" />{property.investment_term} {t('property.months')}</span></div>
                </div>
                <div className="w-full h-px bg-slate-100 my-6" />
                <div className="mb-6">
                  <div className="flex justify-between text-sm mb-2"><span className="text-slate-500 uppercase tracking-wider text-xs">{t('property.fundingProgress')}</span><span className="text-brand-secondary font-mono font-semibold">{progress.toFixed(1)}%</span></div>
                  <div className="h-3 bg-slate-100 overflow-hidden"><motion.div initial={{ width: 0 }} animate={{ width: `${progress}%` }} transition={{ delay: 0.5, duration: 0.8 }} className="h-full bg-brand-primary" /></div>
                  <div className="flex justify-between text-xs mt-2"><span className="text-slate-400">{property.tokens_sold?.toLocaleString()} {t('property.sold')}</span><span className="text-slate-400">{property.total_tokens?.toLocaleString()} {t('property.total')}</span></div>
                </div>
                {property.status === 'active' ? (
                  <Button onClick={handleInvest} className="w-full h-14 bg-brand-primary hover:bg-brand-primary-hover text-brand-secondary font-semibold tracking-wide rounded-none" data-testid="invest-btn"><Wallet className="w-5 h-5 mr-2" />{t('property.investNow')}</Button>
                ) : (
                  <Button disabled className="w-full h-14 bg-slate-100 text-slate-400 cursor-not-allowed rounded-none">{property.status === 'sold_out' ? t('property.soldOut') : t('property.notAvailable')}</Button>
                )}
                {isAuthenticated && !isKYCApproved && (
                  <div className="mt-4 p-4 bg-amber-50 border border-amber-200">
                    <div className="flex items-start gap-2"><AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" /><div><p className="text-sm text-amber-700 font-semibold">{t('property.kycRequired')}</p><p className="text-xs text-amber-600 mt-1">{t('property.kycRequiredSubtitle')}</p><Button onClick={() => navigate('/kyc')} size="sm" variant="ghost" className="mt-2 text-amber-700 hover:text-amber-800 hover:bg-amber-100 p-0 h-auto rounded-none">{t('property.completeKyc')} →</Button></div></div>
                  </div>
                )}
                <div className="mt-4 flex items-center gap-2 text-xs text-slate-400"><Shield className="w-4 h-4" /><span>{t('property.protectedBy')}</span></div>
              </motion.div>
            </div>
          </div>
        </div>
      </main>
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />

      {/* Invest Modal */}
      <Dialog open={investOpen} onOpenChange={setInvestOpen}>
        <DialogContent className="sm:max-w-[440px] bg-white border-slate-200 rounded-none" data-testid="invest-modal">
          <DialogHeader className="bg-brand-secondary -mx-6 -mt-6 px-6 py-4 mb-4">
            <DialogTitle className="text-xl font-heading text-white">{t('invest.title')} {property.name}</DialogTitle>
            <DialogDescription className="text-white/60">{t('invest.subtitle')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            <div className="space-y-2">
              <Label className="text-slate-600 font-semibold">{t('invest.amount')} ({currencyInfo.code})</Label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-primary font-bold">{currencyInfo.symbol}</span>
                <Input type="number" value={investAmount} onChange={(e) => setInvestAmount(e.target.value)}
                  placeholder={`Min ${formatPrice(property.min_investment)}`}
                  className="h-14 pl-10 bg-white border-slate-200 focus:border-brand-primary text-brand-secondary text-lg font-mono rounded-none"
                  data-testid="invest-amount-input" />
              </div>
            </div>
            {investAmount && (
              <div className="p-4 bg-[#F8FAFC] border border-slate-200 space-y-2">
                <div className="flex justify-between text-sm"><span className="text-slate-500">{t('invest.tokensToReceive')}</span><span className="text-brand-secondary font-mono font-semibold">{tokensToReceive} {property.token_symbol}</span></div>
                <div className="flex justify-between text-sm"><span className="text-slate-500">{t('invest.expectedReturn')}</span><span className="text-emerald-600 font-mono font-semibold">{formatPrice(expectedReturn, { decimals: 2 })}</span></div>
              </div>
            )}
            <Button onClick={submitInvestment} disabled={investLoading || !investAmount}
              className="w-full h-14 bg-brand-primary hover:bg-brand-primary-hover text-brand-secondary font-semibold tracking-wide rounded-none" data-testid="confirm-invest-btn">
              {investLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CheckCircle2 className="w-5 h-5 mr-2" />{t('invest.confirm')}</>}
            </Button>
            <p className="text-xs text-slate-400 text-center">{t('invest.terms')}</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
